import Atom from 'lib/components/icons/Atom';
import Book from 'lib/components/icons/Book';
import Desk from 'lib/components/icons/Desk';
import Som from 'lib/components/icons/Som';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/student-dashboard.scss'
import Gift from 'lib/components/icons/Gift';
import { TFunction } from 'i18next';
import StudentFaq from './StudentFaq';
import { observer, useLocalObservable } from 'mobx-react';
import { action, computed, observable, makeObservable, makeAutoObservable } from 'mobx';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { httpClient } from 'domain/utils/httpClient';
import swal from 'sweetalert2';
import { PaymentPurpose, PromoStatus } from 'domain/enums';

function multiTranslate(t: TFunction, text: string, count: number): string {
  const rem = count % 10;

  if (rem == 1) {
    return t(text + '1', { count });
  }
  if (rem >= 5) {
    return t(text, { count });
  }
  else {
    if (count < 21 && count > 10) {
      return t(text, { count });
    }
    return t(text + '<5', { count });
  }
}

class PaymentIframeDialogState {
  isOpened: boolean = false;
  iframeUrl: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this)
  }

  setOpened(iframeUrl: string) {
    this.iframeUrl = iframeUrl;
    this.isOpened = true;
  }

  setClosed() {
    this.isOpened = false;
  }
}

interface CreatePaymentRequest {
  Purpose: PaymentPurpose;
  DebugSecret: string | undefined;
  PromoCode: string | undefined;
}

interface PaymentPromoCheckApiRequest {
  Purpose: PaymentPurpose;
  PromoCode: string;
}

interface PaymentPromoCheckApiResponse {
  PromoStatus: PromoStatus;
  PromoDiscountPercent: number;
  PromoOldPrice: number;
  PromoNewPrice: number;
}

const dialogState = new PaymentIframeDialogState();

interface PaymentIframeDialogProps {

}

function PaymentIframeDialogComponent(props: PaymentIframeDialogProps) {
  return (
    <React.Fragment>
      <Dialog
        open={dialogState.isOpened}
        fullWidth={true}
      >
        <DialogContent>
          <iframe src={dialogState.iframeUrl} width={"100%"} style={{ minWidth: "100%", height: "80vh" }} frameBorder={0}></iframe>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { dialogState.setClosed(); }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

const PaymentIframeDialog = observer(PaymentIframeDialogComponent);

interface DashboardStats {
  CompletedLessons: number;
  TotalLessons: number;
  ExamCount: number;
  HomeworkCount: number;
}

function StudentDashboardComponent() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    httpClient.axios.get<DashboardStats>('/api/student/dashboard-stats')
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  async function clickBuy(purpose: PaymentPurpose) {
    const { value: promoCode } = await swal.fire({
      title: "У Вас есть промокод?",
      input: "text",
      showCancelButton: true,
    });

    if (promoCode) {
      const promoCheckReq: PaymentPromoCheckApiRequest = { Purpose: purpose, PromoCode: promoCode };
      const promoCheckResult = await httpClient.axios.getWithSweetAlert<PaymentPromoCheckApiResponse>("/api/payment/promo/check", { params: promoCheckReq });
      const promoInfo = promoCheckResult.data;
      switch (promoInfo.PromoStatus) {
        case PromoStatus.None:
        case PromoStatus.NotValid:
          await swal.fire({ icon: 'error', text: "Неверный промокод" });
          return;
        case PromoStatus.Valid:
          let html = `<div>Старая цена - ${promoInfo.PromoOldPrice}</div>`;
          html += `<div>Скидка - ${promoInfo.PromoDiscountPercent}%</div>`;
          html += `<div>Новая цена - <strong>${promoInfo.PromoNewPrice}</strong></div>`;
          await swal.fire({
            icon: 'success', title: "Детали промокода",
            html,
            confirmButtonText: "Купить",
            allowEscapeKey: false,
            allowOutsideClick: false,
          });
          break;
      }
    }

    const url = `/api/payment/create`;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let debugSecret: string | undefined = undefined;
    if (params["debugSecret"]) {
      debugSecret = params["debugSecret"];
    }
    const data: CreatePaymentRequest = { Purpose: purpose, DebugSecret: debugSecret, PromoCode: promoCode };
    const result = await httpClient.axios.postWithSweetAlert<{ RedirectUrl: string }>(url, data);
    swal.close();
    dialogState.setOpened(result.data.RedirectUrl);
  }

  return (
    <div className="student-dashboard">
      <PaymentIframeDialog />
      <div className="t-Assignments">
        <div className="t-top">
          <div className="t-top-header">
            <p className="t-top-title">{t('dashboard.students-overview')}</p>
          </div>
          <div className="row ovrvw-container">
            <div className="col">
              <div className="ovrvw-block courses">
                <span><i className="fas fa-chalkboard-teacher"></i></span>
                <h3>{stats?.CompletedLessons ?? '—'}</h3>
                <p>{t('sidebar.completed')}</p>
              </div>
            </div>
            <div className="col">
              <div className="ovrvw-block students">
                <span><i className="fas fa-book-reader"></i></span>
                <h3>{stats?.ExamCount ?? '—'}</h3>
                <p>{t('dashboard.total-exams')}</p>
              </div>
            </div>
            <div className="col">
              <div className="ovrvw-block lessons">
                <span><i className="fas fa-layer-group"></i></span>
                <h3>{stats?.TotalLessons ?? '—'}</h3>
                <p>{t('dashboard.total-lessons')}</p>
              </div>
            </div>
            <div className="col">
              <div className="ovrvw-block balance">
                <span><i className="fas fa-tasks"></i></span>
                <h3>{stats?.HomeworkCount ?? '—'}</h3>
                <p>{t('dashboard.my-homeworks')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='student-dashboard-info'>
        <StudentFaq className='student-left' />

        <div className="student-right">
          <div className="package">
            <div className='package-left'>
              <div className='package-header'>{t('dashboard.package.economy')}</div>
              <div className='package-list'>
                <div className='package-list-item'><Book />{multiTranslate(t, 'dashboard.package.lessons', 4)}</div>
                {/* <div className='package-list-item'><Gift />{t('dashboard.package.gift-lesson')}</div> */}
                <div className='package-list-item'><Desk />{multiTranslate(t, 'dashboard.package.homeworks', 5)}</div>
                <div className='package-list-item'> <Atom />{multiTranslate(t, 'dashboard.package.progress-tests', 1)}</div>
              </div>
            </div>
            <div className='package-right'>
              <div className='package-price'>
                <div className='green-price'> 38$/3290<Som /></div>
              </div>
              <button className='package-button' onClick={() => { clickBuy(PaymentPurpose.SubscriptionEconomy); }}>{t('dashboard.package.buy')}</button>
            </div>
          </div>
          <div className="package">
            <div className='package-left'>
              <div className='package-header'>{t('dashboard.package.standard')}</div>
              <div className='package-list'>
                <div className='package-list-item'><Book />{multiTranslate(t, 'dashboard.package.lessons', 8)}</div>
                {/* <div className='package-list-item'><Gift />{t('dashboard.package.gift-lesson')}</div> */}
                <div className='package-list-item'><Desk /> {multiTranslate(t, 'dashboard.package.homeworks', 9)}</div>
                <div className='package-list-item'> <Atom />{multiTranslate(t, 'dashboard.package.progress-tests', 2)}</div>
              </div>
            </div>
            <div className='package-right'>
              <div className='package-price'>
                <div className='green-price'> 70$/6090<Som /></div>
              </div>
              <button className='package-button' onClick={() => { clickBuy(PaymentPurpose.SubscriptionStandard); }}>{t('dashboard.package.buy')}</button>
            </div>
          </div>
          <div className="package">
            <div className='package-left'>
              <div className='package-header'>{t('dashboard.package.standard-optimum')}</div>
              <div className='package-list'>
                <div className='package-list-item'><Book />{multiTranslate(t, 'dashboard.package.lessons', 12)}</div>
                {/* <div className='package-list-item'><Gift />{t('dashboard.package.gift-lesson')}</div> */}
                <div className='package-list-item'><Desk /> {multiTranslate(t, 'dashboard.package.homeworks', 13)}</div>
                <div className='package-list-item'> <Atom />{multiTranslate(t, 'dashboard.package.progress-tests', 3)}</div>
              </div>
            </div>
            <div className='package-right'>
              <div className='package-price'>
                <div className='green-price'> 95$/8290<Som /></div>
              </div>
              <button className='package-button' onClick={() => { clickBuy(PaymentPurpose.SubscriptionOptimal); }}>{t('dashboard.package.buy')}</button>
            </div>
          </div>
          <div className="package">
            <div className='package-left'>
              <div className='package-header'>{t('dashboard.package.intensive')}</div>
              <div className='package-list'>
                <div className='package-list-item'><Book />{multiTranslate(t, 'dashboard.package.lessons', 20)}</div>
                {/* <div className='package-list-item'><Gift />{t('dashboard.package.gift-lesson')}</div> */}
                <div className='package-list-item'><Desk /> {multiTranslate(t, 'dashboard.package.homeworks', 21)}</div>
                <div className='package-list-item'> <Atom />{multiTranslate(t, 'dashboard.package.progress-tests', 5)}</div>
              </div>
            </div>
            <div className='package-right'>
              <div className='package-price'>
                <div className='green-price'> 149$/12990<Som /></div>
              </div>
              <button className='package-button' onClick={() => { clickBuy(PaymentPurpose.SubscriptionIntensive); }}>{t('dashboard.package.buy')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(StudentDashboardComponent);